"use client";

import { type KeyboardEvent, type Ref, useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ArrowUpToLine } from "lucide-react";
import { cn } from "@/lib/utils";
import type { OrderCue } from "@/hooks/use-order-hold";
import { useReturnFocus } from "@/components/ui/dialog";
import { SheetShell } from "@/components/odds-screen/live/top-menus";
import { americanText, parseAmericanBound } from "@/components/optimizer/odds-range";

export const LIVE_DOT = <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />;

export const SEARCH_ICON = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
);

export function SearchField({
  inputRef,
  value,
  onChange,
  onBlur,
  onKeyDown,
  label,
  name,
  className,
}: {
  inputRef?: Ref<HTMLInputElement>;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  label: string;
  name?: string;
  className?: string;
}) {
  return (
    <label className={cn("group flex items-center gap-2 rounded-md border border-surface-line bg-surface-base pl-2.5 pr-2 text-[13px] text-zinc-500 hover:border-[#45484e] focus-within:border-primary/50", className)}>
      {SEARCH_ICON}
      <input
        ref={inputRef}
        type="search"
        name={name}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        placeholder="Search"
        aria-label={label}
        aria-keyshortcuts="/"
        className="min-w-0 flex-1 bg-transparent text-foreground placeholder:text-zinc-500 focus:outline-none [&::-webkit-search-cancel-button]:hidden"
      />
      {value === "" && <span aria-hidden="true" className="data-num flex h-[18px] min-w-[18px] items-center justify-center rounded border border-surface-line px-1 text-[10px] text-zinc-500 group-focus-within:hidden">/</span>}
    </label>
  );
}

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

export function segGroupClass(tall = false): string {
  return cn("flex flex-shrink-0 items-center gap-0.5 rounded-lg bg-white/[0.04] p-0.5", tall ? "h-9" : "h-8");
}

export function segClass(active: boolean, tall = false): string {
  return cn(
    "flex flex-shrink-0 items-center gap-1.5 rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
    tall ? "h-8 px-3 text-[13px]" : "h-7 px-2.5 text-[12px]",
    active ? "bg-white/[0.10] text-foreground" : "text-zinc-400 hover:text-foreground",
  );
}

export function chipClass(active: boolean, tall = false): string {
  return cn(
    "tap flex flex-shrink-0 items-center justify-center gap-1.5 rounded-md px-2.5 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
    tall ? "h-9 text-[13px]" : "h-8 text-[12px]",
    active ? "bg-primary/10 text-primary" : "bg-white/[0.04] text-zinc-300 hover:bg-white/[0.08] hover:text-foreground",
  );
}

export function quietClass(active: boolean, tall = false): string {
  return cn(
    "tap flex flex-shrink-0 items-center justify-center gap-1.5 rounded-md px-2 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
    tall ? "h-9" : "h-8",
    active ? "bg-primary/[0.08] text-primary" : "text-zinc-300 hover:bg-white/[0.04] hover:text-foreground",
  );
}

export function HiddenCountButton({ count, noun, onClick, tall = false, className }: { count: number; noun: "bet" | "row"; onClick: () => void; tall?: boolean; className?: string }) {
  if (count <= 0) return null;
  return (
    <button type="button" onClick={onClick} aria-label={`${count} hidden ${count === 1 ? noun : `${noun}s`}. Manage hidden`} className={cn(quietClass(false, tall), "data-num whitespace-nowrap", className)}>
      {count} hidden
    </button>
  );
}

export type SegmentedOption<T extends string> = { readonly id: T; readonly label: React.ReactNode; readonly ariaLabel?: string };

export function Segmented<T extends string>({
  label, value, options, onChange, tall = false, stretch = false, className,
}: {
  label: string;
  value: T;
  options: readonly SegmentedOption<T>[];
  onChange: (id: T) => void;
  tall?: boolean;
  stretch?: boolean;
  className?: string;
}) {
  return (
    <div role="group" aria-label={label} className={cn(segGroupClass(tall), stretch && "w-full", className)}>
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          aria-pressed={value === option.id}
          aria-label={option.ariaLabel}
          onClick={() => { if (value !== option.id) onChange(option.id); }}
          className={cn(segClass(value === option.id, tall), stretch && "flex-1 justify-center")}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("data-num px-1.5 pb-1 pt-3 text-[10px] font-medium uppercase tracking-[0.06em] text-zinc-500", className)}>{children}</div>;
}

export function Switch({
  checked, onChange, title, detail, tall = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  title: string;
  detail?: string;
  tall?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={cn("flex w-full items-center justify-between gap-3 rounded-md px-1.5 text-left hover:bg-white/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40", tall ? "py-3" : "py-2")}
    >
      <span className="flex min-w-0 flex-col">
        <span className="text-[13px] font-semibold text-foreground">{title}</span>
        {detail && <span className="data-num text-[11px] text-zinc-500">{detail}</span>}
      </span>
      <span aria-hidden="true" className={cn("relative h-5 w-[34px] flex-shrink-0 rounded-full transition-colors duration-150", checked ? "bg-primary" : "bg-surface-elevated")}>
        <span className={cn("absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition-transform duration-150 motion-reduce:transition-none", checked && "translate-x-[14px]")} />
      </span>
    </button>
  );
}

export function MenuRow({
  active, onClick, tall = false, children, meta, role = "menuitemradio", icon,
}: {
  active: boolean;
  onClick: () => void;
  tall?: boolean;
  children: React.ReactNode;
  meta?: React.ReactNode;
  role?: "menuitemradio" | "menuitemcheckbox" | "option";
  icon?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role={role}
      aria-checked={role === "option" ? undefined : active}
      aria-selected={role === "option" ? active : undefined}
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-2.5 text-left text-[13px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
        tall ? "h-11" : "h-8",
        active ? "bg-primary/10 text-primary" : "text-zinc-300 hover:bg-white/[0.06] hover:text-foreground",
      )}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {meta !== undefined && <span className="data-num text-[11px] text-zinc-500">{meta}</span>}
    </button>
  );
}

export function FieldRow({
  label, active = false, children, below, tall = false,
}: {
  label: string;
  active?: boolean;
  children?: React.ReactNode;
  below?: React.ReactNode;
  tall?: boolean;
}) {
  return (
    <div className={cn("px-2.5", tall ? "py-2.5" : "py-1.5")}>
      <div className="flex min-h-8 items-center justify-between gap-3">
        <span className={cn("min-w-0 whitespace-nowrap text-[13px] font-medium", active ? "text-foreground" : "text-zinc-300")}>{label}</span>
        {children && <span className="flex flex-shrink-0 items-center gap-1.5">{children}</span>}
      </div>
      {below}
    </div>
  );
}

const STEP_BUTTON = "flex h-7 w-7 items-center justify-center rounded-md text-[15px] leading-none text-zinc-400 transition-colors hover:bg-white/[0.06] hover:text-foreground disabled:opacity-30 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

export function Stepper({
  value, min, max, onChange, label, active = false, unit = "", text,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  label: string;
  active?: boolean;
  unit?: string;
  text?: string;
}) {
  return (
    <span role="group" aria-label={label} className={cn(segGroupClass(), "h-8 gap-0")}>
      <button type="button" aria-label={`Decrease ${label}`} disabled={value <= min} onClick={() => onChange(Math.max(min, value - 1))} className={STEP_BUTTON}>−</button>
      <span aria-live="polite" className={cn("data-num text-center text-[13px] font-semibold tabular-nums", text !== undefined ? "min-w-10 px-1" : unit ? "w-9" : "w-6", active ? "text-primary" : "text-foreground")}>{text ?? `${value}${unit}`}</span>
      <button type="button" aria-label={`Increase ${label}`} disabled={value >= max} onClick={() => onChange(Math.min(max, value + 1))} className={STEP_BUTTON}>+</button>
    </span>
  );
}

export const FIELD_CLASS = "data-num h-8 w-12 rounded-md bg-white/[0.04] px-1.5 text-center text-[13px] font-medium tabular-nums text-foreground placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-primary/50 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none";

export function NumberField({
  value, placeholder, min, max, step, onCommit, label, className,
}: {
  value: number | null;
  placeholder: string;
  min: number;
  max: number;
  step?: "any";
  onCommit: (value: number) => void;
  label: string;
  className?: string;
}) {
  const [draft, setDraft] = useState(value === null ? "" : String(value));
  useEffect(() => { setDraft(value === null ? "" : String(value)); }, [value]);
  const commit = () => {
    const parsed = draft.trim() === "" ? Number(placeholder) : Number(draft);
    if (!Number.isFinite(parsed)) { setDraft(value === null ? "" : String(value)); return; }
    const next = Math.min(max, Math.max(min, parsed));
    if (next !== parsed) setDraft(String(next));
    onCommit(next);
  };
  return (
    <input
      type="number"
      inputMode={min < 0 ? undefined : step === "any" ? "decimal" : "numeric"}
      min={min}
      max={max}
      step={step}
      value={draft}
      placeholder={placeholder}
      aria-label={label}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); commit(); } }}
      className={cn(FIELD_CLASS, className)}
    />
  );
}

export function OddsField({
  value, placeholder, onCommit, label,
}: {
  value: number | null;
  placeholder: string;
  onCommit: (value: number | null) => void;
  label: string;
}) {
  const shown = value === null ? "" : americanText(value);
  const [draft, setDraft] = useState(shown);
  useEffect(() => { setDraft(shown); }, [shown]);
  const commit = () => {
    if (draft.trim() === "") {
      if (value !== null) onCommit(null);
      return;
    }
    const parsed = parseAmericanBound(draft);
    if (parsed === null) { setDraft(shown); return; }
    setDraft(americanText(parsed));
    if (parsed !== value) onCommit(parsed);
  };
  return (
    <input
      type="text"
      autoComplete="off"
      spellCheck={false}
      value={draft}
      placeholder={placeholder}
      aria-label={label}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); commit(); } }}
      className={cn(FIELD_CLASS, "w-14")}
    />
  );
}

export function DualRange({
  min, max, low, high, onCommit, label,
}: {
  min: number;
  max: number;
  low: number;
  high: number;
  onCommit: (next: { low: number; high: number }) => void;
  label: string;
}) {
  const [draft, setDraft] = useState({ low, high });
  useEffect(() => { setDraft({ low, high }); }, [low, high]);
  const span = max - min || 1;
  const left = ((draft.low - min) / span) * 100;
  const right = ((draft.high - min) / span) * 100;
  const commit = () => { if (draft.low !== low || draft.high !== high) onCommit(draft); };
  const thumb = "pointer-events-none absolute inset-0 h-full w-full appearance-none bg-transparent [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-primary [&::-webkit-slider-thumb]:bg-surface-raised [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-primary [&::-moz-range-thumb]:bg-surface-raised";
  return (
    <div role="group" aria-label={label} className="relative h-6 w-full">
      <div className="absolute inset-x-2 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-white/[0.12]" />
      <div className="absolute top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-primary" style={{ left: `calc(8px + (100% - 16px) * ${left / 100})`, right: `calc(8px + (100% - 16px) * ${(100 - right) / 100})` }} />
      <input
        type="range" min={min} max={max} step={1} value={draft.low} aria-label={`${label} minimum`}
        onChange={(event) => setDraft((current) => ({ low: Math.min(Number(event.target.value), current.high), high: current.high }))}
        onPointerUp={commit} onKeyUp={commit} onBlur={commit}
        className={thumb}
      />
      <input
        type="range" min={min} max={max} step={1} value={draft.high} aria-label={`${label} maximum`}
        onChange={(event) => setDraft((current) => ({ low: current.low, high: Math.max(Number(event.target.value), current.low) }))}
        onPointerUp={commit} onKeyUp={commit} onBlur={commit}
        className={thumb}
      />
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder, label, autoFocus }: { value: string; onChange: (value: string) => void; placeholder: string; label: string; autoFocus?: boolean }) {
  return (
    <input
      type="search"
      autoFocus={autoFocus}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      aria-label={label}
      className="mb-1 h-8 w-full rounded-md bg-white/[0.04] px-2.5 text-[13px] text-foreground placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-primary/50"
    />
  );
}

const FILTER_ICON = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 5h18M6 12h12M10 19h4" /></svg>
);

export function FiltersButton({ count, open, onClick, tall }: { count: number; open: boolean; onClick: () => void; tall: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      aria-label={count > 0 ? `Filters (${count} active)` : "Filters"}
      data-tour={tall ? "mobile-filter-drawer" : "advanced-filters"}
      style={tall ? { position: "relative" } : undefined}
      className={cn(quietClass(open, tall), tall && "w-9 px-0")}
    >
      {tall ? FILTER_ICON : <span>Filters</span>}
      {count > 0 && <span className={cn("data-num rounded px-1 text-[11px] font-semibold leading-[18px]", tall ? "absolute -right-0.5 -top-0.5 bg-primary/20 text-primary" : "bg-white/[0.08] text-zinc-300")}>{count}</span>}
    </button>
  );
}

function pausedAge(heldAtMs: number, nowMs: number): string {
  const seconds = Math.max(0, Math.round((nowMs - heldAtMs) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h`;
}

export type LiveLag = "Connecting" | "Reconnecting" | "Delayed" | "Offline";

export function LiveControl({ enabled, heldAtMs, lag = null, sourceAt = null, note = null, onToggle, order, tall, compact = false }: { enabled: boolean; heldAtMs: number | null; lag?: LiveLag | null; sourceAt?: number | null; note?: string | null; onToggle: () => void; order: OrderCue | null; tall: boolean; compact?: boolean }) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const since = !enabled ? heldAtMs : lag !== null ? sourceAt : null;
  useEffect(() => {
    if (since === null) return;
    setNowMs(Date.now());
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [since]);
  const age = since !== null ? pausedAge(since, nowMs) : null;
  const lagging = enabled && lag !== null;
  const detail = lagging ? note ?? (age ? `Data ${age} old` : "Waiting for the live feed") : undefined;
  const pill = (iconOnly: boolean) => cn("tap flex flex-shrink-0 items-center gap-1.5 rounded-md text-[12px] font-medium text-zinc-400 hover:bg-white/[0.04] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40", iconOnly ? "justify-center" : "px-2", tall ? "h-9" : "h-8", iconOnly && (tall ? "w-9" : "w-8"));
  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={enabled}
        aria-label={!enabled ? `Paused${age ? ` ${age}` : ""}, resume the feed` : lagging ? `${lag}, ${detail}, pause the feed` : "Live, pause the feed"}
        title={compact ? (!enabled ? `Paused${age ? ` ${age}` : ""}` : lagging ? `${lag}, ${detail}` : "Live") : detail}
        className={pill(compact)}
      >
        <span className={cn("size-1.5 rounded-full", !enabled ? "bg-zinc-600" : lagging ? "bg-amber-400" : "bg-primary shadow-[0_0_0_3px_rgba(34,211,238,0.15)]")} aria-hidden="true" />
        {!compact && (!enabled ? "Paused" : lagging ? lag : "Live")}
        {!compact && age && <span className="data-num text-[11px] text-zinc-500" aria-hidden="true">{age}</span>}
      </button>
      {order !== null && (
        <button
          type="button"
          onClick={order.release}
          aria-label="Order held while you work a play, re-sort now"
          title="Rows stay put while you scroll or have a play open. Click to re-sort."
          className={cn(pill(compact || tall), (!order.held || !enabled) && "invisible")}
        >
          <ArrowUpToLine size={14} aria-hidden="true" />
          {!compact && !tall && "Order held"}
        </button>
      )}
    </>
  );
}

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
  );
}

export function FilterSheetShell({
  open, onOpenChange, isMobile, activeCount, dirty = false, onReset, onApply, applyLabel = "Apply filters", views, dialogs, children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isMobile: boolean;
  activeCount: number;
  dirty?: boolean;
  onReset: () => void;
  onApply: () => void;
  applyLabel?: string;
  views?: React.ReactNode;
  dialogs?: React.ReactNode;
  children: React.ReactNode;
}) {
  const returnFocus = useReturnFocus({});
  const tall = isMobile;

  const body = (
    <div className="min-h-0 flex-grow overflow-y-auto px-2 pb-2 scrollbar-custom scrollbar-color-primary">
      {children}
    </div>
  );

  const count = activeCount > 0 && (
    <span className="data-num rounded-md bg-primary/10 px-1.5 text-[11px] font-semibold leading-5 text-primary" aria-label={`${activeCount} active ${activeCount === 1 ? "filter" : "filters"}`}>{activeCount}</span>
  );

  const footer = (
    <div className={cn("flex flex-shrink-0 items-center gap-2 border-t border-surface-line px-3", tall ? "py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]" : "h-14")}>
      {dirty && (
        <span role="status" className="flex flex-shrink-0 items-center gap-1.5 pr-1 text-[11px] font-medium text-amber-300">
          <span className="size-1.5 rounded-full bg-amber-400" aria-hidden="true" />
          Unsaved
        </span>
      )}
      <button type="button" onClick={onReset} className={cn(quietClass(false, tall), "ml-auto px-3", tall && "h-11")}>Reset</button>
      <button
        type="button"
        onClick={onApply}
        className={cn(
          "tap flex flex-1 items-center justify-center rounded-md bg-primary px-4 text-[13px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
          tall ? "h-11" : "h-8",
        )}
      >
        {applyLabel}
      </button>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <SheetShell
          open={open}
          title="Filters"
          onClose={() => onOpenChange(false)}
          header={(
            <div className="flex min-h-11 flex-shrink-0 items-center gap-2 px-4">
              <span aria-hidden="true" className="text-[15px] font-semibold text-foreground">Filters</span>
              {count}
              {views}
            </div>
          )}
        >
          {body}
          {footer}
        </SheetShell>
        {dialogs}
      </>
    );
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 motion-reduce:animate-none" />
        <DialogPrimitive.Content
          {...returnFocus}
          aria-describedby={undefined}
          className="odds-black fixed inset-y-0 right-0 z-40 flex w-full flex-col border-l border-surface-line bg-surface-raised shadow-[-20px_0_60px_rgba(0,0,0,0.5)] duration-200 ease-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right motion-reduce:animate-none sm:w-[380px] lg:w-[400px]"
        >
          <div className="flex h-11 flex-shrink-0 items-center gap-2 border-b border-surface-line px-4">
            <DialogPrimitive.Title className="text-[15px] font-semibold text-foreground">Filters</DialogPrimitive.Title>
            {count}
            {views}
            <span className="flex-grow" />
            <DialogPrimitive.Close aria-label="Close filters" className="flex h-8 w-8 items-center justify-center rounded-md text-zinc-400 hover:bg-white/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
              <CloseIcon />
            </DialogPrimitive.Close>
          </div>
          {body}
          {footer}
          {dialogs}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
